<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { GripVertical, Leaf, Pencil, Plus, RefreshCw, Trash2 } from 'lucide-vue-next'
import { formatApiError, useApiClient } from '@yuksalish/api-client'
import { useAuthStore } from '@yuksalish/auth'
import { useDragSort } from '@yuksalish/ui'
import PaginationControls from '../../components/PaginationControls.vue'
import {
  createProductSeason,
  deleteProductSeason,
  listAllProductSeasons,
  updateProductSeason,
} from '../productService'
import type { ProductSeasonOption } from '../types'

const client = useApiClient()
const auth = useAuthStore()
const { t } = useI18n()
const seasons = ref<ProductSeasonOption[]>([])
const loading = ref(false)
const saving = ref(false)
const deletingId = ref<number | null>(null)
const errorMessage = ref('')
const formError = ref('')
const search = ref('')
const editingId = ref<number | null>(null)
const currentPage = ref(1)
const pageSize = ref(10)
let searchTimer: ReturnType<typeof setTimeout> | null = null
const form = reactive({
  code: '',
  name: '',
  is_active: '1',
})

const canUpdate = computed(() => auth.user?.permissions.includes('products.update') ?? false)
const canDelete = computed(() => auth.user?.permissions.includes('products.delete') ?? false)
const permissionHint = computed(() => t('engineering.seasons.permissionHint'))
const {
  draggingId: draggingSeasonId,
  overId: dragOverSeasonId,
  onDragStart: onSeasonDragStart,
  onDragOver: onSeasonDragOver,
  onDrop: onSeasonDrop,
  onDragEnd: onSeasonDragEnd,
} = useDragSort<ProductSeasonOption>(seasons, { enabled: canUpdate })
const filteredSeasons = computed(() => {
  const query = search.value.trim().toLocaleLowerCase()
  if (!query) return seasons.value
  return seasons.value.filter((item) =>
    [item.code, item.name].some((value) =>
      String(value ?? '').toLocaleLowerCase().includes(query),
    ),
  )
})
const visibleSeasons = computed(() => filteredSeasons.value)
const totalPages = computed(() =>
  Math.max(1, Math.ceil(visibleSeasons.value.length / pageSize.value)),
)
const paginatedSeasons = computed(() => {
  const start = (currentPage.value - 1) * pageSize.value
  return visibleSeasons.value.slice(start, start + pageSize.value)
})
const isEditing = computed(() => editingId.value != null)

function resetForm(): void {
  editingId.value = null
  form.code = ''
  form.name = ''
  form.is_active = '1'
  formError.value = ''
}

function startEdit(item: ProductSeasonOption): void {
  editingId.value = item.id
  form.code = item.code
  form.name = item.name
  form.is_active = item.is_active ? '1' : '0'
  formError.value = ''
}

async function loadSeasons(): Promise<void> {
  loading.value = true
  errorMessage.value = ''
  try {
    seasons.value = await listAllProductSeasons(client)
  } catch (error: unknown) {
    seasons.value = []
    errorMessage.value = formatApiError(error)
  } finally {
    loading.value = false
  }
}

async function submit(): Promise<void> {
  if (!canUpdate.value) return
  saving.value = true
  formError.value = ''
  try {
    const payload = {
      code: form.code.trim(),
      name: form.name.trim(),
      is_active: Number(form.is_active),
    }
    if (editingId.value == null) {
      await createProductSeason(client, payload)
    } else {
      await updateProductSeason(client, editingId.value, payload)
    }
    await loadSeasons()
    resetForm()
  } catch (error: unknown) {
    formError.value = formatApiError(error)
  } finally {
    saving.value = false
  }
}

async function removeSeason(item: ProductSeasonOption): Promise<void> {
  if (!canDelete.value) return
  if (!window.confirm(t('engineering.seasons.confirmDelete', { name: item.name }))) return
  deletingId.value = item.id
  errorMessage.value = ''
  try {
    await deleteProductSeason(client, item.id)
    if (editingId.value === item.id) {
      resetForm()
    }
    await loadSeasons()
  } catch (error: unknown) {
    errorMessage.value = formatApiError(error)
  } finally {
    deletingId.value = null
  }
}

onMounted(loadSeasons)
watch(search, () => {
  currentPage.value = 1
  if (searchTimer) clearTimeout(searchTimer)
  searchTimer = setTimeout(() => {
    currentPage.value = Math.min(currentPage.value, totalPages.value)
  }, 300)
})
watch([visibleSeasons, pageSize], () => {
  currentPage.value = Math.min(currentPage.value, totalPages.value)
})
onBeforeUnmount(() => {
  if (searchTimer) clearTimeout(searchTimer)
})
</script>

<template>
  <div class="page-heading">
    <div>
      <p class="eyebrow">{{ t('engineering.seasons.eyebrow') }}</p>
      <h2>{{ t('engineering.seasons.title') }}</h2>
      <p>{{ t('engineering.seasons.description') }}</p>
    </div>
    <div class="summary-tile">
      <Leaf :size="18" aria-hidden="true" />
      <div>
        <strong>{{ seasons.length }}</strong>
        <span>{{ t('engineering.seasons.count') }}</span>
      </div>
    </div>
  </div>

  <section class="workspace">
    <form class="editor-card" @submit.prevent="submit">
      <div class="section-heading">
        <div>
          <p class="eyebrow">{{ isEditing ? t('engineering.seasons.editEyebrow') : t('engineering.seasons.createEyebrow') }}</p>
          <h3>{{ isEditing ? t('engineering.seasons.editTitle') : t('engineering.seasons.createTitle') }}</h3>
        </div>
        <button v-if="isEditing" class="ghost-button" type="button" @click="resetForm">
          {{ t('engineering.action.cancel') }}
        </button>
      </div>

      <p v-if="formError" class="message message--error" role="alert">{{ formError }}</p>
      <p v-if="!canUpdate" class="message message--hint">{{ permissionHint }}</p>

      <div class="form-grid">
        <label>
          <span>{{ t('engineering.seasons.code') }}</span>
          <input v-model="form.code" :disabled="saving || !canUpdate" type="text">
        </label>
        <label>
          <span>{{ t('engineering.seasons.name') }}</span>
          <input v-model="form.name" :disabled="saving || !canUpdate" type="text">
        </label>
        <label>
          <span>{{ t('engineering.seasons.status') }}</span>
          <select v-model="form.is_active" :disabled="saving || !canUpdate">
            <option value="1">{{ t('engineering.seasons.active') }}</option>
            <option value="0">{{ t('engineering.seasons.inactive') }}</option>
          </select>
        </label>
      </div>

      <div class="form-actions">
        <button
          class="primary-button"
          type="submit"
          :disabled="saving || !canUpdate || !form.code.trim() || !form.name.trim()"
          :title="!canUpdate ? permissionHint : undefined"
        >
          <Plus v-if="!isEditing" :size="16" aria-hidden="true" />
          <Pencil v-else :size="16" aria-hidden="true" />
          {{ saving ? t('engineering.seasons.saving') : (isEditing ? t('engineering.seasons.saveChanges') : t('engineering.seasons.create')) }}
        </button>
      </div>
    </form>

    <section class="list-card">
      <div class="list-toolbar">
        <label class="search-field">
          <span>{{ t('engineering.seasons.search') }}</span>
          <input v-model="search" type="search" :placeholder="t('engineering.seasons.searchPlaceholder')">
        </label>
        <button class="ghost-button" type="button" :disabled="loading" @click="loadSeasons">
          <RefreshCw :size="16" aria-hidden="true" />
          {{ t('engineering.action.retry') }}
        </button>
      </div>

      <div v-if="errorMessage" class="message message--error" role="alert">{{ errorMessage }}</div>
      <div v-if="loading" class="table-state">{{ t('engineering.seasons.loading') }}</div>
      <div v-else-if="!visibleSeasons.length" class="table-state">{{ t('engineering.seasons.empty') }}</div>
      <table v-else class="reference-table">
        <thead>
          <tr>
            <th class="drag-column" :title="canUpdate ? t('engineering.action.dragHint') : undefined">
              <span v-if="canUpdate" class="drag-handle drag-handle--header" aria-hidden="true"><GripVertical :size="15" /></span>
            </th>
            <th>{{ t('engineering.seasons.code') }}</th>
            <th>{{ t('engineering.seasons.name') }}</th>
            <th>{{ t('engineering.seasons.status') }}</th>
            <th>{{ t('engineering.catalog.actions') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="item in paginatedSeasons"
            :key="item.id"
            class="sortable-row"
            :class="{ 'sortable-row--over': canUpdate && dragOverSeasonId === item.id && draggingSeasonId !== item.id }"
            @dragover="onSeasonDragOver($event, item)"
            @drop="onSeasonDrop($event, item)"
          >
            <td class="drag-column">
              <span
                v-if="canUpdate"
                class="drag-handle"
                draggable="true"
                :title="t('engineering.action.dragHint')"
                @dragstart="onSeasonDragStart($event, item)"
                @dragend="onSeasonDragEnd"
              >
                <GripVertical :size="15" aria-hidden="true" />
              </span>
            </td>
            <td>
              <strong>{{ item.code }}</strong>
              <small>#{{ item.id }}</small>
            </td>
            <td>{{ item.name }}</td>
            <td>
              <span class="status" :class="item.is_active ? 'status--ready' : 'status--muted'">
                {{ item.is_active ? t('engineering.seasons.active') : t('engineering.seasons.inactive') }}
              </span>
            </td>
            <td class="row-actions">
              <button class="icon-button" type="button" :disabled="!canUpdate" :title="!canUpdate ? permissionHint : undefined" @click="startEdit(item)">
                <Pencil :size="16" aria-hidden="true" />
                {{ t('engineering.seasons.edit') }}
              </button>
              <button class="icon-button icon-button--danger" type="button" :disabled="!canDelete || deletingId === item.id" :title="!canDelete ? permissionHint : undefined" @click="removeSeason(item)">
                <Trash2 :size="16" aria-hidden="true" />
                {{ t('engineering.seasons.remove') }}
              </button>
            </td>
          </tr>
        </tbody>
      </table>
      <PaginationControls
        v-if="!loading && visibleSeasons.length"
        v-model:page="currentPage"
        v-model:page-size="pageSize"
        :total="visibleSeasons.length"
        :page-size-options="[10, 25, 50]"
      />
    </section>
  </section>
</template>

<style scoped>
.page-heading,.section-heading,.list-toolbar,.form-actions,.row-actions{display:flex;gap:12px;align-items:center;justify-content:space-between}
.page-heading{margin-bottom:24px}
.workspace{display:grid;grid-template-columns:minmax(320px,380px) minmax(0,1fr);gap:18px}
.editor-card,.list-card{padding:18px;border:1px solid #d7dfdc;border-radius:8px;background:#fff}
.eyebrow{margin:0 0 4px;color:#66726e;font-size:12px;font-weight:700;text-transform:uppercase}
h2,h3,p{margin:0}
.page-heading>div:first-child p:last-child{margin-top:8px;color:#66726e}
.summary-tile{display:inline-flex;gap:12px;align-items:center;padding:12px 14px;border:1px solid #d7dfdc;border-radius:8px;background:#fff}
.summary-tile strong,td strong{display:block}
.summary-tile span,td small{color:#66726e;font-size:12px}
.form-grid{display:grid;gap:12px;margin-top:16px}
label{display:grid;gap:6px}
label span,th{color:#586660;font-size:12px;font-weight:700;text-transform:uppercase}
input,select,button{min-height:40px;padding:8px 10px;border:1px solid #adb9b4;border-radius:8px;background:#fff;font:inherit}
.primary-button,.ghost-button,.icon-button{display:inline-flex;gap:8px;align-items:center;justify-content:center;width:auto;font-weight:700;cursor:pointer}
.primary-button{border:0;color:#fff;background:#3568a8}
.ghost-button,.icon-button{color:#285b9a}
.icon-button--danger{color:#8b3025}
.form-actions{justify-content:flex-start;margin-top:16px}
.search-field{flex:1}
.search-field input{width:100%}
.message{margin-top:16px;padding:12px 14px;border-radius:8px}
.message--error{border:1px solid #d7a8a1;color:#852d22;background:#fff5f3}
.message--hint{border:1px solid #d7dfdc;color:#66726e;background:#f7faf9}
.list-card table{width:100%;margin-top:16px;border-collapse:collapse}
th,td{padding:12px;border-bottom:1px solid #e1e6e4;text-align:left;vertical-align:middle}
th{background:#f3f6f5}
.drag-column{width:36px;white-space:nowrap}
.drag-handle{display:inline-flex;align-items:center;justify-content:center;width:28px;height:28px;border-radius:6px;color:#8a9691;cursor:grab;touch-action:none}
.drag-handle:hover{color:#285b9a;background:#eef4fb}
.drag-handle--header{cursor:default;color:#b6c0bc}
.drag-handle:active{cursor:grabbing}
.sortable-row--over{box-shadow:inset 3px 0 0 #3568a8;background:#f4f8fd}
.sortable-row--over td{background:transparent}
.status{display:inline-flex;padding:4px 8px;border:1px solid;border-radius:999px;font-size:12px;font-weight:700}
.status--ready{border-color:#86b4a4;color:#236451;background:#eff8f5}
.status--muted{border-color:#c2c9c6;color:#5f6b66;background:#f5f7f6}
.table-state{display:grid;min-height:220px;color:#697671;place-items:center}
button:disabled{cursor:not-allowed;opacity:.55}
@media (max-width:900px){.workspace{grid-template-columns:1fr}}
@media (max-width:640px){.page-heading,.section-heading,.list-toolbar,.row-actions{align-items:stretch;flex-direction:column}.row-actions{white-space:nowrap}}
</style>
